import {defineStore} from 'pinia'
import {computed, nextTick, ref} from 'vue'
import * as Sentry from '@sentry/vue'
import {
  mainProductTourSteps,
} from '@toolify/client/src/modules/productTour/constants/mainProductTourSteps'
import {
  databaseProductTourSteps,
} from '@toolify/client/src/modules/productTour/constants/databaseProductTourSteps'
import {
  documentProductTourSteps,
} from '@toolify/client/src/modules/productTour/constants/documentProductTourSteps'
import {
  resolveProductTourStepAnchor,
} from '@toolify/client/src/modules/productTour/functions/resolveProductTourStepAnchor'
import {
  getProductTourAnchorSelector,
} from '@toolify/client/src/modules/productTour/functions/getProductTourAnchorSelector'
import {IProductTourStep} from '@toolify/client/src/modules/productTour/types/IProductTourStep'
import {ProductTourType} from '@toolify/server/src/enum/ProductTourType'
import {ProductTourStatusType} from '@toolify/server/src/enum/ProductTourStatusType'
import {ProductTourStartSourceType} from '@toolify/server/src/enum/ProductTourStartSourceType'
import {ProductTourAnchorType} from '@toolify/client/src/modules/productTour/enum/ProductTourAnchorType'
import {ProductTourStopReasonType} from '@toolify/client/src/modules/productTour/enum/ProductTourStopReasonType'
import {productTourVersionByType} from '@toolify/client/src/modules/productTour/constants/productTourAutoOffer'
import {persistProductTourState} from '@toolify/client/src/modules/productTour/functions/persistProductTourState'
import {QuickNavigationItemName} from '@toolify/client/src/modules/navigation/enum/QuickNavigationItemName'
import {useAppLayoutStore} from '@toolify/client/src/stores/AppLayoutStore/useAppLayoutStore'

const stepsByTourType: Record<ProductTourType, IProductTourStep[]> = {
  [ProductTourType.Main]: mainProductTourSteps,
  [ProductTourType.Database]: databaseProductTourSteps,
  [ProductTourType.Document]: documentProductTourSteps,
}

interface ILayoutSnapshotBeforeTour {
  isSideNavigationOpen: boolean
  activeQuickNavigationItem: QuickNavigationItemName | null
}

interface IStartOptions {
  source?: ProductTourStartSourceType
}

const statusByStopReason: Partial<Record<ProductTourStopReasonType, ProductTourStatusType>> = {
  [ProductTourStopReasonType.Skipped]: ProductTourStatusType.Skipped,
  [ProductTourStopReasonType.Dismissed]: ProductTourStatusType.Dismissed,
  [ProductTourStopReasonType.RouteChanged]: ProductTourStatusType.Dismissed,
  [ProductTourStopReasonType.Completed]: ProductTourStatusType.Completed,
}

function addBreadcrumb(message: string, data: Record<string, unknown>) {
  Sentry.addBreadcrumb({
    category: 'product-tour',
    level: 'info',
    message,
    data,
  })
}

export const useProductTourStore = defineStore('productTour', () => {
  const appLayoutStore = useAppLayoutStore()

  // null while no tour is running or being started — components that render
  // tour-only anchors (e.g. the table canvas markers) gate on this, so it is set
  // before the anchors are probed and cleared as soon as the tour ends
  const activeTourType = ref<ProductTourType | null>(null)
  const steps = computed(() => {
    return activeTourType.value === null ? [] : stepsByTourType[activeTourType.value]
  })
  const currentStepIndex = ref<number | null>(null)
  const startSource = ref<ProductTourStartSourceType | null>(null)
  // Indexes of the steps whose anchors were visible when the tour started — the
  // progress dots and the step counter derive from this snapshot, so their
  // denominator stays stable while the DOM changes underneath a running tour.
  const availableStepIndexes = ref<number[]>([])
  // isSideNavigationOpen and activeQuickNavigationItem are persisted user
  // preferences (useStorage) — the main tour overrides them to reach its sidebar
  // anchors and restores them when the tour ends.
  let layoutSnapshotBeforeTour: ILayoutSnapshotBeforeTour | null = null

  const isActive = computed(() => {
    return currentStepIndex.value !== null
  })

  const isAutoOffered = computed(() => {
    return startSource.value === ProductTourStartSourceType.Auto
  })

  const currentStep = computed<IProductTourStep | null>(() => {
    if (currentStepIndex.value === null) {
      return null
    }
    return steps.value[currentStepIndex.value] ?? null
  })

  const currentStepNumber = computed(() => {
    if (currentStepIndex.value === null) {
      return 0
    }
    const position = availableStepIndexes.value.indexOf(currentStepIndex.value)
    return position === -1 ? 0 : position + 1
  })

  const totalStepCount = computed(() => {
    return availableStepIndexes.value.length
  })

  // First/last AVAILABLE step — steps whose anchors are absent would otherwise
  // leave a dead "Back" button on the first step the user actually sees, or a
  // "Next" on the real finale that silently closes the tour
  const isFirstStep = computed(() => {
    if (currentStepIndex.value === null) {
      return false
    }
    return findAvailableStepIndex(currentStepIndex.value - 1, -1) === null
  })

  const isLastStep = computed(() => {
    if (currentStepIndex.value === null) {
      return false
    }
    return findAvailableStepIndex(currentStepIndex.value + 1, 1) === null
  })

  function isStepAvailable(step: IProductTourStep): boolean {
    if (step.anchor === null) {
      return true
    }
    return resolveProductTourStepAnchor(step) !== null
  }

  // Navigation walks the start-time snapshot, re-checking visibility so a step
  // whose anchor disappeared mid-tour is still skipped
  function findAvailableStepIndex(fromIndex: number, direction: 1 | -1): number | null {
    const candidateIndexes = availableStepIndexes.value.filter((index) => {
      return direction === 1 ? index >= fromIndex : index <= fromIndex
    })
    if (direction === -1) {
      candidateIndexes.reverse()
    }
    return candidateIndexes.find(index => isStepAvailable(steps.value[index])) ?? null
  }

  function countAvailableAnchoredSteps(tourType: ProductTourType): number {
    return stepsByTourType[tourType].filter(step => step.anchor !== null && isStepAvailable(step)).length
  }

  async function start(tourType: ProductTourType = ProductTourType.Main, options: IStartOptions = {}) {
    activeTourType.value = tourType
    startSource.value = options.source ?? ProductTourStartSourceType.HelpMenu
    if (tourType === ProductTourType.Main) {
      // ??= — restarting a tour on top of a running one keeps the original preferences
      layoutSnapshotBeforeTour ??= {
        isSideNavigationOpen: appLayoutStore.isSideNavigationOpen,
        activeQuickNavigationItem: appLayoutStore.activeQuickNavigationItem,
      }
      appLayoutStore.isSideNavigationOpen = true
      // The sidebar steps anchor inside the Favourites strategy only
      appLayoutStore.activeQuickNavigationItem = QuickNavigationItemName.Favourites
    }
    // Let the sidebar strategy / the tour-only anchors (canvas markers) render
    // before the anchors are probed
    await nextTick()
    availableStepIndexes.value = steps.value.flatMap((step, index) => {
      return isStepAvailable(step) ? [index] : []
    })
    currentStepIndex.value = availableStepIndexes.value[0] ?? null
    if (currentStepIndex.value === null) {
      stop(ProductTourStopReasonType.NoAnchors)
      return
    }
    addBreadcrumb('start', {
      tourType,
      source: startSource.value,
      availableStepCount: availableStepIndexes.value.length,
    })
  }

  function restoreLayoutSnapshot() {
    if (layoutSnapshotBeforeTour === null) {
      return
    }
    appLayoutStore.isSideNavigationOpen = layoutSnapshotBeforeTour.isSideNavigationOpen
    appLayoutStore.activeQuickNavigationItem = layoutSnapshotBeforeTour.activeQuickNavigationItem
    layoutSnapshotBeforeTour = null
  }

  function recordOutcome(reason: ProductTourStopReasonType) {
    const tourType = activeTourType.value
    if (tourType === null) {
      return
    }
    const stepReached = currentStep.value?.name ?? null
    const availableStepCount = availableStepIndexes.value.length
    addBreadcrumb('stop', {
      tourType,
      reason,
      source: startSource.value,
      stepReached,
      availableStepCount,
    })
    const status = statusByStopReason[reason]
    if (!status) {
      return
    }
    persistProductTourState(tourType, {
      status,
      version: productTourVersionByType[tourType],
      at: new Date().toISOString(),
      source: startSource.value ?? ProductTourStartSourceType.HelpMenu,
      stepReached,
      availableStepCount,
    })
  }

  function clearActiveTour() {
    currentStepIndex.value = null
    activeTourType.value = null
    startSource.value = null
  }

  function stop(reason: ProductTourStopReasonType = ProductTourStopReasonType.Dismissed) {
    recordOutcome(reason)
    clearActiveTour()
    restoreLayoutSnapshot()
  }

  function goToNextStep() {
    if (currentStepIndex.value === null) {
      return
    }
    const nextIndex = findAvailableStepIndex(currentStepIndex.value + 1, 1)
    if (nextIndex === null) {
      stop(ProductTourStopReasonType.Completed)
      return
    }
    currentStepIndex.value = nextIndex
  }

  function goToPreviousStep() {
    if (currentStepIndex.value === null) {
      return
    }
    const previousIndex = findAvailableStepIndex(currentStepIndex.value - 1, -1)
    if (previousIndex === null) {
      return
    }
    currentStepIndex.value = previousIndex
  }

  function finishAndOpenCreateResource() {
    // The create-resource modal is local state of the Favourites sidebar strategy —
    // restoring the pre-tour quick-navigation strategy here would unmount the modal
    // the user just asked for, so only the sidebar-open preference is restored.
    recordOutcome(ProductTourStopReasonType.Completed)
    if (layoutSnapshotBeforeTour !== null) {
      appLayoutStore.isSideNavigationOpen = layoutSnapshotBeforeTour.isSideNavigationOpen
      layoutSnapshotBeforeTour = null
    }
    clearActiveTour()
    // The modal is local state of the sidebar strategy, so until the tour gets a
    // dedicated signal it is opened through its trigger button.
    document.querySelector<HTMLElement>(
      getProductTourAnchorSelector(ProductTourAnchorType.CreateResourceButton),
    )?.click()
  }

  return {
    isActive,
    isAutoOffered,
    activeTourType,
    currentStep,
    currentStepNumber,
    totalStepCount,
    isFirstStep,
    isLastStep,
    countAvailableAnchoredSteps,
    start,
    stop,
    goToNextStep,
    goToPreviousStep,
    finishAndOpenCreateResource,
  }
})
